import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SalesReportViewer = ({ open, onClose, reportData, filters, onDownload, onPrint }) => {
  if (!reportData) return null;

  const { summary, details, vatBreakdown, paymentMethods, metadata } = reportData;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          📊 Sales Report
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Report Header */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Period: {metadata?.reportPeriod || `${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generated on: {metadata?.generatedAt ? formatDateTime(metadata.generatedAt) : formatDateTime(new Date())}
          </Typography>
          <Chip
            label={`Grouped by: ${filters.groupBy}`}
            size="small"
            sx={{ mt: 1, mr: 1 }}
          />
          <Chip
            label={`Payment: ${filters.paymentMethod}`}
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                color: 'white',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Total Sales
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                €{Number(summary?.totalSales || 0).toFixed(2)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
                color: 'white',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Total VAT
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                €{Number(summary?.totalVAT || 0).toFixed(2)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                color: 'white',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Transactions
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {summary?.totalTransactions || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                color: 'white',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Avg. Sale
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                €{Number(summary?.averageSale || 0).toFixed(2)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* VAT Breakdown */}
        {filters.includeVAT && vatBreakdown && Object.keys(vatBreakdown).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              💰 VAT Breakdown
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>VAT Rate</strong></TableCell>
                    <TableCell align="right"><strong>Subtotal (Excl. VAT)</strong></TableCell>
                    <TableCell align="right"><strong>VAT Amount</strong></TableCell>
                    <TableCell align="right"><strong>Total (Incl. VAT)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(vatBreakdown).map(([rate, data]) => (
                    <TableRow key={rate} hover>
                      <TableCell>
                        <Chip label={`${rate}%`} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="right">€{Number(data.subtotal || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">€{Number(data.vat || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <strong>€{Number(data.total || 0).toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Payment Methods */}
        {paymentMethods && Object.keys(paymentMethods).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              💳 Payment Methods
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(paymentMethods).map(([method, data]) => (
                <Grid item xs={6} sm={4} md={3} key={method}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {method}
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        €{Number(data.amount || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {Number(data.percentage || 0).toFixed(1)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Sales Details Table */}
        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            📋 Sales Details
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Date & Time</strong></TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Receipt #</strong></TableCell>
                  {filters.includeCustomers && (
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Customer</strong></TableCell>
                  )}
                  <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Subtotal</strong></TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>VAT</strong></TableCell>
                  <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Total</strong></TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Payment</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details && details.length > 0 ? (
                  details.map((sale, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{formatDateTime(sale.date)}</TableCell>
                      <TableCell>
                        <Chip label={sale.receiptNumber} size="small" variant="outlined" />
                      </TableCell>
                      {filters.includeCustomers && (
                        <TableCell>{sale.customerName || 'Walk-in'}</TableCell>
                      )}
                      <TableCell align="right">€{Number(sale.subtotal || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">€{Number(sale.vat || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <strong>€{Number(sale.total || 0).toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip label={sale.paymentMethod} size="small" color="primary" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={filters.includeCustomers ? 7 : 6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No transactions found for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
        <Button
          onClick={() => onDownload('excel')}
          startIcon={<ExcelIcon />}
          variant="outlined"
          color="success"
        >
          Excel
        </Button>
        <Button
          onClick={() => onDownload('pdf')}
          startIcon={<PdfIcon />}
          variant="outlined"
          color="error"
        >
          PDF
        </Button>
        <Button
          onClick={onPrint}
          startIcon={<PrintIcon />}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesReportViewer;